import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { PLATFORM } from "../env";
import type { Driver } from "../types";

interface Props {
  onOpenDetail: (id: number) => void;
}

interface DriversPage {
  items: Driver[];
  count?: number;
  total?: number;
}

const LIMIT = 50;

const statusColor = (status: string): "success" | "warning" | "error" | "default" => {
  if (status === "active") return "success";
  if (status === "suspended") return "warning";
  if (status === "blocked") return "error";
  return "default";
};

export default function Drivers({ onOpenDetail }: Props) {
  const authApi = useAuthApi();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedDriverIds, setSelectedDriverIds] = useState<number[]>([]);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadDrivers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
    if (statusFilter !== "all") params.set("status", statusFilter);
    try {
      const data = await authApi<DriversPage>(`${PLATFORM}/drivers?${params}`);
      setDrivers(data?.items ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load drivers");
    } finally {
      setLoading(false);
    }
  }, [authApi, offset, statusFilter]);

  useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  const handleStatusChange = (e: SelectChangeEvent) => {
    setStatusFilter(e.target.value);
    setOffset(0);
    setSelectedDriverIds([]);
  };

  const selectedSet = useMemo(() => new Set(selectedDriverIds), [selectedDriverIds]);
  const allVisibleSelected = drivers.length > 0 && drivers.every((d) => selectedSet.has(d.id));
  const someVisibleSelected = drivers.some((d) => selectedSet.has(d.id)) && !allVisibleSelected;

  const toggleDriverSelection = (driverId: number) => {
    setSelectedDriverIds((prev) => (
      prev.includes(driverId) ? prev.filter((id) => id !== driverId) : [...prev, driverId]
    ));
  };

  const toggleVisibleSelection = () => {
    setSelectedDriverIds((prev) => {
      const visibleIds = drivers.map((d) => d.id);
      if (visibleIds.length === 0) return prev;
      const everyVisibleSelected = visibleIds.every((id) => prev.includes(id));
      if (everyVisibleSelected) return prev.filter((id) => !visibleIds.includes(id));
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return [...next];
    });
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedDriverIds];
    if (ids.length === 0) return;
    setBulkDeleting(true);
    setError(null);
    setNotice(null);
    const results = await Promise.allSettled(
      ids.map((id) => authApi(`${PLATFORM}/drivers/${id}`, { method: "DELETE" }))
    );

    const failedIds = results.flatMap((result, index) => (
      result.status === "rejected" ? [ids[index]] : []
    ));
    const deletedCount = ids.length - failedIds.length;

    if (failedIds.length > 0) {
      setError(
        `Deleted ${deletedCount} driver${deletedCount === 1 ? "" : "s"}; ` +
        `failed to delete ${failedIds.length} driver${failedIds.length === 1 ? "" : "s"}.`
      );
    } else {
      setNotice(`Deleted ${deletedCount} driver${deletedCount === 1 ? "" : "s"}.`);
    }

    setSelectedDriverIds(failedIds);
    setConfirmBulkDeleteOpen(false);
    setBulkDeleting(false);
    await loadDrivers();
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={handleStatusChange}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
            <MenuItem value="blocked">Blocked</MenuItem>
          </Select>
        </FormControl>
        <Button
          color="error"
          variant="contained"
          size="small"
          disabled={selectedDriverIds.length === 0 || bulkDeleting}
          onClick={() => setConfirmBulkDeleteOpen(true)}
        >
          Delete selected ({selectedDriverIds.length})
        </Button>
      </Box>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      {!loading && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleVisibleSelection}
                    inputProps={{ "aria-label": "Select all drivers on page" }}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers.map((d) => (
                <TableRow
                  key={d.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("button,input,label")) return;
                    onOpenDetail(d.id);
                  }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={selectedSet.has(d.id)}
                      onChange={() => toggleDriverSelection(d.id)}
                      inputProps={{ "aria-label": `Select driver ${d.name}` }}
                    />
                  </TableCell>
                  <TableCell>{d.id}</TableCell>
                  <TableCell>{d.name}</TableCell>
                  <TableCell>{d.email}</TableCell>
                  <TableCell>{d.phone ?? "—"}</TableCell>
                  <TableCell>
                    <Chip label={d.status} color={statusColor(d.status)} size="small" />
                  </TableCell>
                  <TableCell>{new Date(d.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {drivers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">No drivers found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              size="small"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={drivers.length < LIMIT}
              onClick={() => setOffset((o) => o + LIMIT)}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      <Dialog open={confirmBulkDeleteOpen} onClose={() => !bulkDeleting && setConfirmBulkDeleteOpen(false)}>
        <DialogTitle>Delete selected drivers?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedDriverIds.length} selected driver
            {selectedDriverIds.length === 1 ? "" : "s"}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained" disabled={bulkDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
